/**
 * OrganizationSettingsTab
 * =======================
 *
 * General org settings + governance policies + SSO for Team+
 * organizations. Only shown (and only resolvable) for users who belong
 * to an organization-tier plan. Edits require an admin/owner role.
 *
 * Backend (via the ``organizations`` redux-saga namespace):
 *   GET  /organization/mine           -> orgs + capabilities (gating)
 *   GET  /organization/<id>           -> { name, slug, settings, policies, ... }
 *   PUT  /organization/<id>           -> { name, slug, settings, policies }
 *   GET  /organization/<id>/sso       -> { sso (secret masked), redirect_uri }
 *   PUT  /organization/<id>/sso       -> save OIDC config
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Stack, TextField, MenuItem, Button, Divider,
  Switch, FormControlLabel, CircularProgress, Alert,
} from '@mui/material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useSnackbar } from 'notistack';

import {
  fetchRequest as fetchMine,
  clear as clearMine,
} from '../../redux/actions/organizations/mine';
import {
  fetchRequest as fetchDetail,
  clear as clearDetail,
} from '../../redux/actions/organizations/detail';
import {
  fetchRequest as updateRequest,
  clear as clearUpdate,
} from '../../redux/actions/organizations/update';
import {
  fetchRequest as ssoUpdateRequest,
  clear as clearSsoUpdate,
} from '../../redux/actions/organizations/ssoUpdate';

// Mirrors VALID_MEMBER_ROLES on the BE. ``publisher`` = member who
// may also publish/manage the org's marketplace listings; ``billing``
// = member who may also see/manage billing.
const MEMBER_ROLES = ['viewer', 'member', 'publisher', 'billing', 'admin'];

const DEFAULT_POLICIES = {
  allowed_email_domains: [],
  default_member_role: 'member',
  two_factor_required: false,
};

const DEFAULT_SSO = {
  enabled: false,
  provider_label: '',
  issuer_url: '',
  client_id: '',
  client_secret_set: false,
  email_domains: [],
};

const OrganizationSettingsTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const mineSlice = useSelector((s) => s.organization_mine) || {};
  const detailSlice = useSelector((s) => s.organization_detail) || {};
  const updateSlice = useSelector((s) => s.organization_update) || {};
  const ssoUpdateSlice = useSelector((s) => s.organization_sso_update) || {};

  const [initialized, setInitialized] = useState(false);
  const [orgId, setOrgId] = useState('');

  const [form, setForm] = useState({
    name: '', slug: '', billing_email: '', default_timezone: 'UTC',
  });
  const [policies, setPolicies] = useState(DEFAULT_POLICIES);
  const [domainsText, setDomainsText] = useState('');

  // SSO config (its own endpoints + Save button; the client secret is
  // write-only, the GET only says whether one is stored).
  const [sso, setSso] = useState(DEFAULT_SSO);
  const [ssoSecret, setSsoSecret] = useState('');
  const [ssoDomainsText, setSsoDomainsText] = useState('');
  const [ssoRedirectUri, setSsoRedirectUri] = useState('');

  // Discover the user's orgs first.
  useEffect(() => {
    dispatch(clearDetail());
    dispatch(clearUpdate());
    dispatch(clearSsoUpdate());
    dispatch(clearMine());
    dispatch(fetchMine());
    setInitialized(true);
  }, [dispatch]);

  const orgs = useMemo(
    () => (mineSlice.response?.organizations || []).filter((o) => o.is_team_plus),
    [mineSlice.response],
  );
  const loadedOrgs = initialized && !mineSlice.isLoading
    && Boolean(mineSlice.response || mineSlice.error);

  useEffect(() => {
    if (initialized && mineSlice.error) {
      enqueueSnackbar('Could not load organizations', { variant: 'error' });
    }
  }, [initialized, mineSlice.error, enqueueSnackbar]);

  useEffect(() => {
    if (!loadedOrgs || orgId || !orgs.length) return;
    setOrgId(orgs[0].uid);
  }, [loadedOrgs, orgs, orgId]);

  const canManage = useMemo(
    () => Boolean(orgs.find((x) => x.uid === orgId)?.can_manage),
    [orgs, orgId],
  );

  // Load the selected org (and, for admins, its SSO config) whenever the
  // selection changes.
  useEffect(() => {
    if (!orgId) return;
    dispatch(fetchDetail({ orgId, includeSso: canManage }));
  }, [orgId, canManage, dispatch]);

  // Mirror the loaded org into the form state (also re-runs after a save,
  // since the update saga refreshes the detail slice).
  useEffect(() => {
    const payload = detailSlice.response;
    if (!payload?.org) return;
    const o = payload.org;
    const s = o.settings || {};
    setForm({
      name: o.name || '',
      slug: o.slug || '',
      billing_email: s.billing_email || '',
      default_timezone: s.default_timezone || 'UTC',
    });
    const p = { ...DEFAULT_POLICIES, ...(o.policies || {}) };
    setPolicies(p);
    setDomainsText((p.allowed_email_domains || []).join(', '));
    // SSO config is admin-only; a failed read just resets the section.
    if (payload.ssoRequested) {
      if (payload.sso) {
        const cfg = { ...DEFAULT_SSO, ...(payload.sso.sso || {}) };
        setSso(cfg);
        setSsoDomainsText((cfg.email_domains || []).join(', '));
        setSsoRedirectUri(payload.sso.redirect_uri || '');
        setSsoSecret('');
      } else {
        setSso(DEFAULT_SSO);
      }
    }
  }, [detailSlice.response]);

  // A successful SSO save returns the stored config (secret masked).
  useEffect(() => {
    const payload = ssoUpdateSlice.response;
    if (!payload) return;
    const cfg = { ...DEFAULT_SSO, ...(payload.sso || {}) };
    setSso(cfg);
    setSsoDomainsText((cfg.email_domains || []).join(', '));
    if (payload.redirect_uri) setSsoRedirectUri(payload.redirect_uri);
    setSsoSecret('');
  }, [ssoUpdateSlice.response]);

  const setPolicy = (key, value) => setPolicies((p) => ({ ...p, [key]: value }));

  const saving = Boolean(updateSlice.isLoading);
  const ssoSaving = Boolean(ssoUpdateSlice.isLoading);

  const handleSave = () => {
    const body = {
      name: form.name,
      slug: form.slug,
      settings: {
        billing_email: form.billing_email,
        default_timezone: form.default_timezone,
      },
      policies: {
        ...policies,
        allowed_email_domains: domainsText
          .split(',').map((d) => d.trim()).filter(Boolean),
      },
    };
    dispatch(updateRequest({ orgId, body, includeSso: canManage }));
  };

  const handleSsoSave = () => {
    const body = {
      enabled: !!sso.enabled,
      provider_label: sso.provider_label || '',
      issuer_url: sso.issuer_url || '',
      client_id: sso.client_id || '',
      email_domains: ssoDomainsText
        .split(',').map((d) => d.trim()).filter(Boolean),
    };
    if (ssoSecret.trim()) body.client_secret = ssoSecret.trim();
    dispatch(ssoUpdateRequest({ orgId, body }));
  };

  if (!loadedOrgs || (orgs.length > 0 && !orgId)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>
    );
  }

  if (!orgId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          You don't belong to an organization yet. Upgrade to a Team plan to create one.
        </Typography>
      </Paper>
    );
  }

  const disabled = !canManage || saving;

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <CorporateFareIcon fontSize="small" color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Organization</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        General details and governance policies for your organization.
      </Typography>

      {orgs.length > 1 && (
        <TextField
          select size="small" label="Organization" value={orgId}
          onChange={(e) => setOrgId(e.target.value)} sx={{ minWidth: 280, mb: 3 }}
        >
          {orgs.map((o) => <MenuItem key={o.uid} value={o.uid}>{o.name || o.uid}</MenuItem>)}
        </TextField>
      )}

      {!canManage && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You can view these settings, but only an organization admin can change them.
        </Alert>
      )}

      {/* General */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>General</Typography>
      <Stack spacing={2} sx={{ mb: 3, maxWidth: 520 }}>
        <TextField size="small" label="Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={disabled} />
        <TextField size="small" label="Slug" value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })} disabled={disabled} />
        <TextField size="small" label="Billing email" value={form.billing_email}
          onChange={(e) => setForm({ ...form, billing_email: e.target.value })} disabled={disabled} />
        <TextField size="small" label="Default timezone" value={form.default_timezone}
          onChange={(e) => setForm({ ...form, default_timezone: e.target.value })} disabled={disabled} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Policies */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Policies</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Governance controls applied across the organization.
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <TextField
          size="small" label="Allowed email domains"
          placeholder="acme.com, contractors.acme.com"
          helperText="Comma-separated. Empty = allow any domain. Enforced on invites."
          value={domainsText} onChange={(e) => setDomainsText(e.target.value)} disabled={disabled}
        />
        <TextField
          select size="small" label="Default member role" value={policies.default_member_role}
          onChange={(e) => setPolicy('default_member_role', e.target.value)} disabled={disabled}
          sx={{ maxWidth: 220 }}
        >
          {MEMBER_ROLES.map((r) => <MenuItem key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</MenuItem>)}
        </TextField>

        <FormControlLabel
          control={<Switch checked={!!policies.two_factor_required}
            onChange={(e) => setPolicy('two_factor_required', e.target.checked)} disabled={disabled} />}
          label="Require two-factor authentication"
        />
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={disabled}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Single sign-on */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <VpnKeyIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Single sign-on (SSO)</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Connect your identity provider (OpenID Connect — Okta, Azure AD,
        Google Workspace, Keycloak, …). Once enabled, anyone whose email
        domain matches — including newly invited members — signs in through
        your provider from the login page.
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <FormControlLabel
          control={<Switch checked={!!sso.enabled}
            onChange={(e) => setSso((s) => ({ ...s, enabled: e.target.checked }))}
            disabled={disabled} />}
          label="Enable SSO login"
        />
        <TextField
          size="small" label="Provider name"
          placeholder="Okta"
          helperText="Shown on the login button ('Continue with Okta')."
          value={sso.provider_label}
          onChange={(e) => setSso((s) => ({ ...s, provider_label: e.target.value }))}
          disabled={disabled}
        />
        <TextField
          size="small" label="Issuer URL"
          placeholder="https://your-company.okta.com"
          helperText="The OIDC issuer — discovery happens at <issuer>/.well-known/openid-configuration."
          value={sso.issuer_url}
          onChange={(e) => setSso((s) => ({ ...s, issuer_url: e.target.value }))}
          disabled={disabled}
        />
        <TextField
          size="small" label="Client ID"
          value={sso.client_id}
          onChange={(e) => setSso((s) => ({ ...s, client_id: e.target.value }))}
          disabled={disabled}
        />
        <TextField
          size="small" type="password" label="Client secret"
          placeholder={sso.client_secret_set ? '•••••••• (stored — leave blank to keep)' : ''}
          helperText="Write-only: it is never shown again after saving."
          value={ssoSecret}
          onChange={(e) => setSsoSecret(e.target.value)}
          disabled={disabled}
          autoComplete="new-password"
        />
        <TextField
          size="small" label="SSO email domains"
          placeholder="acme.com, contractors.acme.com"
          helperText="Comma-separated. Logins from these domains are routed to your provider."
          value={ssoDomainsText}
          onChange={(e) => setSsoDomainsText(e.target.value)}
          disabled={disabled}
        />
        {ssoRedirectUri && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Register this redirect URI at your identity provider:{' '}
            <Box component="code" sx={{ fontSize: 12, wordBreak: 'break-all' }}>
              {ssoRedirectUri}
            </Box>
          </Alert>
        )}
      </Stack>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSsoSave} disabled={disabled || ssoSaving}>
          {ssoSaving ? 'Saving…' : 'Save SSO configuration'}
        </Button>
      </Box>
    </Paper>
  );
};

export default OrganizationSettingsTab;
